/**
 * RPA Job Dashboard data service.
 *
 * Merges real-time Firestore subscriptions into a unified RpaJob[] stream:
 *   1. appointmentDayRuns — RevolutionEHR patient pulls
 *   2. eligibilityChecks — one subscription per entry in `RPA_VENDORS`
 *      (VSP, EyeMed, …), filtered by `type == '<vendorId>'`
 *
 * Fanning out one subscription per vendor keeps the existing Firestore
 * composite index (`type + status + checkedAt`) usable unchanged, since
 * each subscription is a pure equality filter on `type`. Adding a new
 * vendor here is automatic: it picks up every entry from the registry.
 */

import {
  query, where, orderBy, onSnapshot, Timestamp,
  type QueryConstraint,
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import * as Sentry from '@sentry/react'
import { functions } from '../config'
import { appointmentDayRunsCollection, orgEligibilityChecksCollection } from './firestore'
import type { AppointmentDayRun, EligibilityCheckDoc, RpaJob, RpaJobStatus, RpaJobContext } from '../types'
import { RPA_VENDORS, type RpaVendorConfig, type RpaVendorId } from '../../shared/rpaRegistry'

// ── Date range presets ──────────────────────────────────────────────

export type DatePreset = 'today' | 'yesterday' | 'last7' | 'last30' | 'older'

export interface DateRange {
  start: Date
  end: Date
}

export function dateRangeFromPreset(preset: DatePreset): DateRange {
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const todayEnd = new Date(todayStart)
  todayEnd.setDate(todayEnd.getDate() + 1) // exclusive upper bound (midnight tomorrow)

  switch (preset) {
    case 'today':
      return { start: todayStart, end: todayEnd }
    case 'yesterday': {
      const yday = new Date(todayStart)
      yday.setDate(yday.getDate() - 1)
      return { start: yday, end: todayStart }
    }
    case 'last7': {
      const d = new Date(todayStart)
      d.setDate(d.getDate() - 6)
      return { start: d, end: todayEnd }
    }
    case 'last30': {
      const d = new Date(todayStart)
      d.setDate(d.getDate() - 29)
      return { start: d, end: todayEnd }
    }
    case 'older': {
      const end = new Date(todayStart)
      end.setDate(end.getDate() - 30)
      return { start: new Date(0), end }
    }
  }
}

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last7: 'Last 7 Days',
  last30: 'Last 30 Days',
  older: 'Older',
}

// ── Status normalization ────────────────────────────────────────────

/**
 * Map a run's raw Firestore status to the dashboard's normalized status.
 * Pre-completion statuses (`pending`, `pulling`) split based on whether
 * the lane's worker has actually started the task: no `startedAt` ⇒ Waiting,
 * `startedAt` populated ⇒ In Progress. Terminal statuses are unaffected.
 *
 * Exported for unit tests; the runtime path goes through normalizeRun /
 * normalizeCheck below.
 */
export function normalizeRunStatus(
  status: AppointmentDayRun['status'],
  hasStartedAt: boolean,
): RpaJobStatus {
  switch (status) {
    case 'pending':
    case 'pulling':
      return hasStartedAt ? 'in_progress' : 'waiting'
    case 'pulled':
    case 'processing':
    case 'completed':
      return 'success'
    case 'failed':
    case 'error':
      return 'error'
    case 'timeout':
      return 'timeout'
    default:
      return hasStartedAt ? 'in_progress' : 'waiting'
  }
}

export function normalizeCheckStatus(status: string, hasStartedAt: boolean): RpaJobStatus {
  switch (status) {
    case 'pending': return hasStartedAt ? 'in_progress' : 'waiting'
    case 'success': return 'success'
    case 'error': return 'error'
    case 'timeout': return 'timeout'
    default: return hasStartedAt ? 'in_progress' : 'waiting'
  }
}

// ── Timestamp helpers ───────────────────────────────────────────────

function firestoreTimestampToDate(val: unknown): Date | null {
  if (!val) return null
  if (val instanceof Timestamp) return val.toDate()
  if (typeof val === 'object' && val !== null && 'toDate' in val) {
    return (val as { toDate(): Date }).toDate()
  }
  if (typeof val === 'string') return new Date(val)
  return null
}

// ── Normalization ───────────────────────────────────────────────────

const TERMINAL: ReadonlySet<RpaJobStatus> = new Set(['success', 'error', 'timeout'])

/**
 * Where a row sits in the newest-first order. A row with a start orders by
 * it; an active row with no start is newest (still queued); a TERMINAL row
 * with no start — finalized by the failsafe net before its task booted, or
 * dispatch-failed — orders by its completion time, so it no longer sits pinned
 * at the top. Stored `startedAt` stays null on such docs; this is a display
 * rule, not a write.
 */
function rowSortAt(status: RpaJobStatus, initiatedAt: Date | null, completedAt: Date | null): Date | null {
  if (initiatedAt) return initiatedAt
  return TERMINAL.has(status) ? completedAt : null
}

export function normalizeRun(run: AppointmentDayRun, rawData: Record<string, unknown>): RpaJob {
  const startedAt = firestoreTimestampToDate(rawData.startedAt)
  // "Initiated" = the worker's actual task-start time: "—" while a run is
  // queued (startedAt present-but-null), filled once the worker stamps the
  // start. Legacy docs predate the field entirely (absent, not null) — keep
  // their old initiated value (lastRetryAt → createdAt) so their column isn't
  // blanked.
  const initiatedAt = rawData.startedAt === undefined
    ? firestoreTimestampToDate(rawData.lastRetryAt)
      ?? firestoreTimestampToDate(rawData.createdAt)
      ?? new Date(run.createdAt)
    : startedAt
  const completedAt = firestoreTimestampToDate(rawData.pulledAt ?? rawData.failedAt)
  // Duration = completedAt − initiatedAt, so it matches the columns shown:
  // execution-only for new docs (initiatedAt = startedAt), queue+run for legacy
  // docs (initiatedAt = old value). "—" while queued (no initiatedAt) or on a
  // negative diff from a stale completedAt.
  const rawDurationMs = initiatedAt && completedAt
    ? completedAt.getTime() - initiatedAt.getTime()
    : null
  const durationMs = rawDurationMs != null && rawDurationMs >= 0 ? rawDurationMs : null

  const context: RpaJobContext = {
    kind: 'revolution',
    clinicId: run.clinicId,
    appointmentDate: run.appointmentDate,
    patientCount: run.patientCount ?? 0,
  }

  // Legacy docs (startedAt field absent) keep their old "In Progress" reading;
  // only new docs (field present) split Waiting/In-Progress on the stamped
  // start. Mirrors the initiatedAt back-compat branch above.
  const hasStartedAt = rawData.startedAt === undefined ? true : startedAt !== null
  const status = normalizeRunStatus(run.status, hasStartedAt)

  return {
    id: `run:${run.id}`,
    jobType: 'revolution',
    status,
    clinicId: run.clinicId,
    initiatedAt,
    completedAt,
    sortAt: rowSortAt(status, initiatedAt, completedAt),
    durationMs,
    error: run.error ?? null,
    context,
    sourceCollection: 'appointmentDayRuns',
    sourceDocId: run.id,
    raw: run,
  }
}

export function normalizeCheck(
  doc: EligibilityCheckDoc,
  rawData: Record<string, unknown>,
  vendor: RpaVendorConfig,
): RpaJob {
  const startedAt = firestoreTimestampToDate(rawData.startedAt)
  // New checks carry startedAt (null while queued → "—"); legacy checks predate
  // the field (absent) → keep the old fallback to the check creation time.
  const initiatedAt = rawData.startedAt === undefined
    ? new Date(doc.checkedAt)
    : startedAt
  const completedAt = firestoreTimestampToDate(rawData.completedAt)
  // Duration = completedAt − initiatedAt (see normalizeRun): execution-only for
  // new checks, queue+run for legacy. "—" while queued or on a negative diff.
  const rawDurationMs = initiatedAt && completedAt
    ? completedAt.getTime() - initiatedAt.getTime()
    : null
  const durationMs = rawDurationMs != null && rawDurationMs >= 0 ? rawDurationMs : null

  const identity = rawData.identity as { firstName?: string; lastName?: string; dob?: string } | undefined
  const reqSub = doc.requestSubscriber
  const firstName = identity?.firstName ?? reqSub?.firstName ?? ''
  const lastName = identity?.lastName ?? reqSub?.lastName ?? ''
  const payerName = (rawData.payerName as string) ?? ''

  const context: RpaJobContext = {
    kind: 'eligibility',
    rpaVendor: vendor.id,
    rpaVendorDisplay: vendor.displayName,
    patientName: `${firstName} ${lastName}`.trim(),
    dob: identity?.dob ?? reqSub?.dateOfBirth ?? null,
    payerName,
  }

  // Legacy checks (startedAt field absent) keep their old "In Progress"
  // reading; only new checks (field present) split Waiting/In-Progress on the
  // stamped start. Mirrors the initiatedAt back-compat branch above.
  const hasStartedAt = rawData.startedAt === undefined ? true : startedAt !== null
  const status = normalizeCheckStatus(doc.status ?? 'pending', hasStartedAt)

  return {
    id: `check:${doc.id}`,
    jobType: 'vision',
    status,
    clinicId: doc.clinicId ?? '',
    initiatedAt,
    completedAt,
    sortAt: rowSortAt(status, initiatedAt, completedAt),
    durationMs,
    error: (rawData.error as string) ?? doc.errorDetails ?? null,
    context,
    sourceCollection: 'eligibilityChecks',
    sourceDocId: doc.id,
    raw: doc,
  }
}

// ── Real-time subscription ──────────────────────────────────────────

export function subscribeToRpaJobs(
  orgId: string,
  clinicId: string | null,
  isAdmin: boolean,
  dateRange: DateRange,
  onData: (jobs: RpaJob[]) => void,
): () => void {
  let runJobs: RpaJob[] = []
  const vendorCheckJobs = new Map<RpaVendorId, RpaJob[]>()
  const vendorReady = new Set<RpaVendorId>()
  let runsReady = false

  function checksReady(): boolean {
    return vendorReady.size >= RPA_VENDORS.length
  }

  function merge() {
    if (!runsReady || !checksReady()) return
    const flatChecks = RPA_VENDORS.flatMap(v => vendorCheckJobs.get(v.id) ?? [])
    const all = [...runJobs, ...flatChecks]
    // Newest first; queued jobs (no start yet) sort as most-recent so they
    // stay at the top, while a terminal row that never started orders by its
    // completion (sortAt). (This order is re-applied by the table's own sort.)
    all.sort((a, b) => {
      const aT = a.sortAt?.getTime() ?? Infinity
      const bT = b.sortAt?.getTime() ?? Infinity
      return aT === bT ? 0 : aT > bT ? -1 : 1
    })
    onData(all)
  }

  // ── appointmentDayRuns subscription ─────────────────────────────
  const runConstraints: QueryConstraint[] = []
  if (!isAdmin && clinicId) {
    runConstraints.push(where('clinicId', '==', clinicId))
  }
  runConstraints.push(
    where('createdAt', '>=', Timestamp.fromDate(dateRange.start)),
    where('createdAt', '<=', Timestamp.fromDate(dateRange.end)),
    orderBy('createdAt', 'desc'),
  )

  const runsQuery = query(appointmentDayRunsCollection(orgId), ...runConstraints)
  const unsubRuns = onSnapshot(runsQuery, (snap) => {
    runJobs = snap.docs.map(d => {
      const typed = d.data()
      return normalizeRun(typed, typed as unknown as Record<string, unknown>)
    })
    runsReady = true
    merge()
  }, (err) => {
    Sentry.captureException(err)
    runsReady = true
    merge()
  })

  // ── eligibilityChecks subscriptions — one per RPA vendor ─────────
  // One equality-on-type query per vendor lets the existing composite
  // index (type + status + checkedAt) serve every call, vs. a single
  // `type in [...]` query which would conflict with the status filter
  // (Firestore allows only one `in` per query).
  const startIso = dateRange.start.toISOString()
  const endIso = dateRange.end.toISOString()

  const unsubVendorChecks = RPA_VENDORS.map((vendor) => {
    const checkConstraints: QueryConstraint[] = []
    if (!isAdmin && clinicId) {
      checkConstraints.push(where('clinicId', '==', clinicId))
    }
    checkConstraints.push(
      where('type', '==', vendor.id),
      where('status', 'in', ['pending', 'success', 'error', 'timeout']),
      where('checkedAt', '>=', startIso),
      where('checkedAt', '<=', endIso),
      orderBy('checkedAt', 'desc'),
    )
    const vendorQuery = query(orgEligibilityChecksCollection(orgId), ...checkConstraints)
    return onSnapshot(vendorQuery, (snap) => {
      vendorCheckJobs.set(vendor.id, snap.docs.map(d => {
        const rawData = d.data() as Record<string, unknown>
        const doc = { id: d.id, ...rawData } as EligibilityCheckDoc
        return normalizeCheck(doc, rawData, vendor)
      }))
      vendorReady.add(vendor.id)
      merge()
    }, (err) => {
      Sentry.captureException(err)
      // Mark ready even on error so we don't deadlock the merge.
      vendorReady.add(vendor.id)
      merge()
    })
  })

  return () => {
    unsubRuns()
    unsubVendorChecks.forEach(u => u())
  }
}

// ── Re-run parameter extraction ─────────────────────────────────────

export function extractRerunParams(job: RpaJob, orgId: string): { fn: string; params: Record<string, unknown> } | null {
  if (job.sourceCollection === 'appointmentDayRuns') {
    const run = job.raw as AppointmentDayRun
    return {
      fn: 'initiateAppointmentPull',
      params: { orgId, clinicId: run.clinicId, appointmentDate: run.appointmentDate },
    }
  }

  if (job.sourceCollection === 'eligibilityChecks') {
    const rawDoc = job.raw as EligibilityCheckDoc
    const rawAny = rawDoc as unknown as Record<string, unknown>
    const identity = rawAny.identity as {
      firstName?: string; lastName?: string; dob?: string
      policyNumber?: string; memberId?: string
    } | undefined

    if (!identity) return null

    // Retry functions are intentionally per-vendor: each vendor's retry flow
    // may diverge (different validation, error handling, UX). We dispatch by
    // the job's vendor ID here so adding a new RPA vendor is one map entry +
    // one new cloud function, not a rewrite of the retry plumbing.
    const vendorId = job.context.kind === 'eligibility' ? job.context.rpaVendor : null
    if (!vendorId) return null

    const retryFnByVendor: Record<RpaVendorId, string> = {
      vsp: 'initiateVspCheck',
      eyemed: 'initiateEyemedCheck',
    }

    // Carry the original doctor name forward so the retry preserves the
    // provider the user picked on the source check. Without this, the
    // initiate*Check handlers would build an empty-doctor identity and
    // runRpaPath's fallback would stamp the vendor default instead.
    const storedIdentity = rawAny.identity as { doctorName?: string } | undefined
    const doctorName = storedIdentity?.doctorName ?? ''

    return {
      fn: retryFnByVendor[vendorId],
      params: {
        orgId,
        clinicId: rawDoc.clinicId ?? '',
        subscriber: {
          firstName: identity.firstName ?? '',
          lastName: identity.lastName ?? '',
          memberId: identity.memberId || identity.policyNumber || '',
          dateOfBirth: identity.dob ?? '',
        },
        doctorName,
        payerName: (rawAny.payerName as string) ?? '',
        appointmentDate: rawDoc.appointmentDate ?? new Date().toISOString().slice(0, 10),
        sourceCheckId: rawDoc.id,
      },
    }
  }

  return null
}

/**
 * Re-runs a job through the callable `extractRerunParams` named. Returns the
 * callable's raw result: `initiateAppointmentPull` answers `{results}`, the
 * vision check callables `{action}` at top level.
 */
export async function rerunJob(
  rerun: { fn: string; params: Record<string, unknown> },
): Promise<Record<string, unknown>> {
  const fn = httpsCallable<unknown, Record<string, unknown>>(functions, rerun.fn)
  const res = await fn(rerun.params)
  return res.data
}
